<template>
  <div class="property-behavior">
    <!-- No espelho, a Cardinalidade é o exclusivo do dono: gravado como CONDIÇÃO, trava como travaria lá -->
    <ontos-pair-toggle v-if="offersMultiple" :model-value="!!model.multiple" v-bind="pairs.multiple"
                       :disabled="mirrorLocked" :disabled-reason="mirrorLocked ? $tt('property-condition-locked') : ''"
                       @update:model-value="$emit('multiple', $event)">
      <!-- O mínimo e o máximo são regra de quem PREENCHE: numa lista calculada não há quem, nem no
           espelho, cuja lista é feita pelos itens do outro lado -->
      <!-- Um interruptor para cada limite (pedido do usuário): desligado, o que vale no lugar do campo — "0" e
           "Ilimitado" —; ligado, o campo de número do Vuetify, com o − e o +, já com um número. Era uma caixa
           "Ilimitado" com o máximo apagado mostrando um valor velho, e depois o máximo vazio querendo dizer
           "sem limite" — os dois estranhos -->
      <div v-if="!model.computed && kind !== 'mirror'" class="property-behavior-count">
        <div v-for="limit of limits" :key="limit.key" class="property-behavior-limit">
          <v-switch :model-value="limited[limit.key]" :label="$tt(limit.label)" color="primary" density="compact"
                    hide-details @update:model-value="setLimited(limit.key, !!$event)"/>
          <v-number-input v-if="limited[limit.key]" :model-value="numberOf(model[limit.field])"
                          @update:model-value="setLimit(limit.key, $event)"
                          @update:focused="!$event && $nextTick(() => settle(limit.key))" :aria-label="$tt(limit.label)"
                          :min="floorOf(limit.key)" :max="ceilingOf(limit.key)" :precision="0" control-variant="split"
                          density="compact" hide-details
                          class="property-behavior-number"/>
          <span v-else class="text-body-1 text-medium-emphasis">{{ limit.key === 'min' ? 0 : $tt('unlimited') }}</span>
        </div>
      </div>
    </ontos-pair-toggle>

    <!-- Também na lista, onde Obrigatório é ter mínimo (pedido do usuário) — ver o `setRequired` -->
    <ontos-pair-toggle v-if="kind !== 'mirror' && !model.computed" :model-value="requiredShown" v-bind="pairs.required"
                       :disabled="isCondition(original.required)"
                       :disabled-reason="isCondition(original.required) ? $tt('property-condition-locked') : ''"
                       @update:model-value="setRequired($event)"/>
    <ontos-pair-toggle v-if="offersUnique" :model-value="!!model.unique" v-bind="pairs.unique"
                       :disabled="isCondition(original.unique)"
                       :disabled-reason="isCondition(original.unique) ? $tt('property-condition-locked') : ''"
                       @update:model-value="model.unique = $event"/>
    <ontos-pair-toggle v-if="filledSingle && offersIdentifier" :model-value="!!model.identifier" v-bind="pairs.identifier"
                       @update:model-value="setIdentifier($event)"/>

    <ontos-pair-toggle v-if="offersComputed" :model-value="!!model.computed" v-bind="pairs.computed"
                       :disabled="!!computeBlocked" :disabled-reason="computeBlocked"
                       @update:model-value="model.computed = $event"/>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, Watch, toNative} from 'vue-facing-decorator'
import IdeaProperty from '@/types/IdeaProperty'
import PairToggle, {PairSide} from '@/components/shared/PairToggle.vue'
import {isCondition} from '@/model/conditions'
import {isMedia} from '@/utils/mediaTypes'
import {identifierBlocker, MarkKind, MARKS} from '@/utils/propertyDefinition'
import {MessageKey} from '@/i18n'

/** O que a seção mexe no modelo do editor — os do atributo e do relacionamento têm estes campos. */
export type BehaviorModel = {
  type?: string, multiple: boolean, required: any, unique: any, identifier: boolean, computed: boolean,
  /** O máximo vazio é sem limite. */
  minItems: number | string, maxItems: number | string | null,
  /** No relacionamento: o calculado dele não compõe o nome, e o do atributo compõe (`identifierBlocker`). */
  relationship?: boolean
}

type Pair = { label: string, off: PairSide, on: PairSide }

/**
 * O mínimo e o máximo de itens no modelo, a partir da propriedade gravada. Os dois editores faziam cada
 * um o seu, e o do atributo lia o "sem limite" de um campo que não existe (`itemCount.maxItems`).
 */
export function behaviorModelOf(value: IdeaProperty): Pick<BehaviorModel, 'minItems' | 'maxItems'> {
  const count = value.itemCount
  return count ? {minItems: count.min, maxItems: count.max ?? null} : {minItems: 0, maxItems: null}
}

const strong = (value: any) => value || undefined
const positive = (value: any): number | undefined => Number(value) > 0 ? Number(value) : undefined

/**
 * O que a seção decide, como vai gravado — o par do `behaviorModelOf`, igual no atributo e no
 * relacionamento:
 * - obrigatório no que alguém preenche: no valor único, o par; na lista, ter mínimo (pedido do usuário:
 *   mínimo 0 é Opcional, 1 ou mais é Obrigatório);
 * - exclusivo só no valor único; os dois, gravados como CONDIÇÃO, voltam como estão (o par é sim/não e
 *   não sabe dizer `{or: [...]}` — salvar a substituiria);
 * - o mínimo e o máximo só na lista que alguém preenche, e nada quando não dizem nada;
 * - o identificador só quando pode (`identifierBlocker`).
 */
export function behaviorValue(model: BehaviorModel, original: IdeaProperty) {
  const filled = !model.multiple && !model.computed
  const keep = (stored: any, edited: any) => isCondition(stored) ? stored : edited
  const min = positive(model.minItems)
  const max = positive(model.maxItems)
  const required = model.computed ? undefined : model.multiple ? (min ? true : undefined) : strong(model.required)
  return {
    multiple: strong(model.multiple),
    required: keep(original.required, required),
    unique: keep(original.unique, filled ? strong(model.unique) : undefined),
    //Um dos dois pode faltar — sem mínimo, ou sem limite —, e o servidor aceita; o tipo é que exige os dois
    itemCount: model.multiple && !model.computed && (min || max) ? {min, max} as IdeaProperty['itemCount'] : undefined,
    identifier: !identifierBlocker(model) && model.identifier ? true : undefined
  }
}

/**
 * A seção Comportamento da janela de propriedade: vários valores, obrigatório, exclusivo, compõe o nome
 * e calculado, cada um num par de dois lados com a frase do escolhido (pedido do usuário: no lugar das
 * caixas de marcar). Estava escrita igual — com o mínimo e o máximo — no editor de atributo e no de
 * relacionamento.
 *
 * Mexe direto no `model` do editor (os campos dele, e não o objeto): é o editor que observa o modelo e
 * entrega a propriedade. O "Vários valores" sai como evento (`multiple`): o atributo de seleção descarta
 * o padrão ao trocá-lo, e só ele sabe.
 *
 * O lado ligado de cada par tem o ícone e a cor da marca (`MARKS`), os mesmos dos selos da lista.
 */
@Component({name: 'ontos-property-behavior', emits: ['multiple'], components: {'ontos-pair-toggle': PairToggle}})
class PropertyBehavior extends Vue {
  @Prop({type: Object, required: true}) readonly model!: BehaviorModel
  /** A propriedade como estava: o obrigatório e o exclusivo gravados como CONDIÇÃO travam o par. */
  @Prop({type: Object, required: true}) readonly original!: IdeaProperty
  /**
   * `relationship`: o dono — a Cardinalidade e o resto, sem a Unicidade, que é a Cardinalidade da caixa
   * de lá (pedido do usuário). `mirror`: o lado de lá — só a Cardinalidade, derivada do dono; o `model`
   * vem do editor já na visão do espelho, e o `original` é o do dono (ver o `RelationshipInput.behaviorOf`).
   */
  @Prop({type: String, required: true}) readonly kind!: 'attribute' | 'relationship' | 'mirror'
  /** Por que o "Calculado" não se liga — o relacionamento com espelho —, ou vazio. */
  @Prop({type: String, default: ''}) readonly computeBlocked!: string
  /**
   * A frase da Cardinalidade no lugar da de sempre ("Um valor só por item"): no relacionamento, o sentido
   * da associação que sai deste lado — "Cada Canção tem até 1 Gênero musical" (pedido do usuário).
   */
  @Prop({type: String, default: ''}) readonly cardinalityHint!: string

  //exposta ao template: condição gravada trava o par
  isCondition = isCondition

  /** Mídia é um arquivo por item, e o sim/não já é um valor só: nenhum dos dois vira lista. */
  get offersMultiple(): boolean {
    return this.kind !== 'attribute' || (this.model.type !== 'boolean' && !isMedia(this.model.type))
  }

  /** Um valor só, que alguém preenche: onde valem o obrigatório e compor o nome. */
  get filledSingle(): boolean {
    return this.kind !== 'mirror' && !this.model.multiple && !this.model.computed
  }

  /**
   * Só no atributo. No relacionamento, o exclusivo é a Cardinalidade do outro lado — "cada Cliente em um
   * Pedido só" é o "Um valor" da caixa do Cliente —, e mora lá (pedido do usuário).
   */
  get offersUnique(): boolean {
    return this.kind === 'attribute' && this.filledSingle && this.model.type !== 'boolean' && !isMedia(this.model.type)
  }

  /** Os dois limites da lista: o interruptor de cada um, e o campo que ele mostra. */
  limits = [
    {key: 'min', field: 'minItems', label: 'min-items'},
    {key: 'max', field: 'maxItems', label: 'max-items'}
  ] as const

  /**
   * Até onde o campo desce. O piso de cada um é o número que DESFAZ o limite (`setLimit`) — o mínimo em 0 é
   * não ter mínimo, e o máximo em 1 é um valor só —, e o máximo não desce abaixo do mínimo (pedido do
   * usuário).
   */
  floorOf(key: 'min' | 'max'): number {
    return key === 'min' ? 0 : Math.max(1, (this.limited.min && positive(this.model.minItems)) || 0)
  }

  /** Até onde o campo sobe: o mínimo não passa do máximo, quando há máximo (pedido do usuário). */
  ceilingOf(key: 'min' | 'max'): number | undefined {
    return key === 'min' && this.limited.max ? positive(this.model.maxItems) : undefined
  }

  /** O número do campo, que o modelo guarda também como texto (o que veio de um campo de texto). */
  numberOf(value: number | string | null): number | null {
    return value === null || value === '' ? null : Number(value)
  }

  /**
   * O número digitado ou dado pelo − e o +. No piso, o limite se desfaz (pedido do usuário): o mínimo em 0
   * desliga o interruptor, que volta a dizer "0" — é o mesmo que não ter mínimo —, e o máximo em 1 vira
   * "Um valor", que é o que uma lista de no máximo 1 é. Vazio não: é o campo apagado para digitar outro, e
   * desfazer ali atrapalharia a digitação.
   */
  setLimit(key: 'min' | 'max', value: number | null) {
    const previous = this.numberOf(key === 'min' ? this.model.minItems : this.model.maxItems)
    if (key === 'min') this.model.minItems = value === null ? '' : value
    else this.model.maxItems = value
    //Pelo −, um passo abaixo do que estava: o piso desfaz já. Digitado, só ao sair do campo (`settle`) —
    //"12" passa pelo "1", e o máximo viraria um valor só no meio da digitação
    if (value !== null && previous !== null && value === previous - 1) this.settle(key)
  }

  /**
   * O piso desfaz o limite: o mínimo em 0 desliga o interruptor, e o máximo em 1 vira "Um valor". Ao sair do
   * campo, com o número já dentro dos limites — o campo do Vuetify só aceita, digitando, o que cabe entre o
   * `min` e o `max`, e põe no limite o resto ao sair (o máximo nunca abaixo do mínimo).
   */
  settle(key: 'min' | 'max') {
    const value = this.numberOf(key === 'min' ? this.model.minItems : this.model.maxItems)
    if (key === 'min' && value === 0) this.setLimited('min', false)
    if (key === 'max' && value === 1) {
      this.setLimited('max', false)
      this.$emit('multiple', false)
    }
  }

  /** Na lista, Obrigatório é o interruptor do mínimo; no valor único, o que está gravado. */
  get requiredShown(): boolean {
    return this.model.multiple ? this.limited.min : !!this.model.required
  }

  /**
   * Virou lista com Obrigatório: o mínimo liga em 1 — na lista, Obrigatório é ter mínimo, e sem ele o par
   * passaria a dizer Opcional sozinho.
   */
  @Watch('model.multiple')
  onMultipleChanged(multiple: boolean) {
    if (multiple && this.model.required === true && !this.limited.min) this.setLimited('min', true)
  }

  /**
   * Os interruptores ligados. À parte do número, e não "tem número": apagar o campo para digitar outro
   * desligaria o interruptor no meio da digitação. Abrem como está gravado — com o limite, ligados.
   */
  limited: Record<'min' | 'max', boolean> = {min: false, max: false}

  created() {
    this.limited = {min: positive(this.model.minItems) !== undefined, max: positive(this.model.maxItems) !== undefined}
  }

  /**
   * Liga com um número já no campo — o mínimo em 1, o máximo em 10, ou no mínimo se ele for maior —, e
   * desliga apagando o limite, que é o que "sem mínimo" e "sem máximo" sempre foram (`behaviorModelOf`).
   */
  setLimited(key: 'min' | 'max', on: boolean) {
    this.limited[key] = on
    if (key === 'max') {
      this.model.maxItems = on ? Math.max(10, positive(this.model.minItems) || 0) : null
      return
    }
    this.model.minItems = on ? 1 : 0
    //Na lista, o mínimo é o Obrigatório (pedido do usuário) — e o obrigatório que é condição fica como está
    if (this.model.multiple && !isCondition(this.model.required)) this.model.required = on
  }

  /** A Cardinalidade do espelho, quando o exclusivo do dono está gravado como condição. */
  get mirrorLocked(): boolean {
    return this.kind === 'mirror' && isCondition(this.original.unique)
  }

  /** Mídia é arquivo enviado, e não valor que se calcula; o espelho mostra o que o dono guarda. */
  get offersComputed(): boolean {
    return this.kind === 'relationship' || (this.kind === 'attribute' && !isMedia(this.model.type))
  }

  /**
   * O que pode compor o nome dos itens (`identifierBlocker`). O "não é obrigatório" não impede: compor o
   * nome liga o obrigatório junto, que o identificador exige — e a frase do par diz.
   */
  get offersIdentifier(): boolean {
    const blocker = identifierBlocker(this.model)
    return !blocker || blocker === 'not-required'
  }

  setIdentifier(identifier: boolean) {
    this.model.identifier = identifier
    if (identifier && !isCondition(this.model.required)) this.model.required = true
  }

  /** Opcional não compõe o nome: deixa de compô-lo junto. */
  setRequired(required: boolean) {
    this.model.required = required
    if (!required) this.model.identifier = false
    //Na lista, Obrigatório é ter mínimo: o par liga e desliga o interruptor dele (pedido do usuário)
    if (this.model.multiple && this.limited.min !== required) this.setLimited('min', required)
  }

  get pairs(): Record<'multiple' | 'required' | 'unique' | 'identifier' | 'computed', Pair> {
    const pair = (kind: MarkKind, label: MessageKey, off: [MessageKey, MessageKey], on: [MessageKey, MessageKey]): Pair => ({
      label: this.$tt(label) as string,
      off: {label: this.$tt(off[0]) as string, hint: this.$tt(off[1]) as string},
      on: {label: this.$tt(on[0]) as string, hint: this.$tt(on[1]) as string, ...MARKS[kind]}
    })
    const multiple = pair('multiple', 'pair.cardinality', ['pair.single', 'pair.single-hint'], ['pair.multiple', 'pair.multiple-hint'])
    //No relacionamento, a frase da Cardinalidade é o sentido da associação (pedido do usuário)
    if (this.cardinalityHint) {
      multiple.off.hint = this.cardinalityHint
      multiple.on.hint = this.cardinalityHint
    }
    return {
      multiple,
      required: pair('required', 'pair.requiredness', ['pair.optional', 'pair.optional-hint'], ['required', 'pair.required-hint']),
      unique: pair('unique', 'pair.uniqueness', ['pair.repeatable', 'pair.repeatable-hint'], ['unique', 'pair.unique-hint']),
      identifier: pair('identifier', 'pair.identification',
          ['pair.outside-name', 'pair.outside-name-hint'], ['pair.in-name', 'pair.in-name-hint']),
      computed: pair('computed', 'pair.filling', ['pair.filled', 'pair.filled-hint'], ['computed', 'pair.computed-hint'])
    }
  }
}

export default toNative(PropertyBehavior)
</script>

<style scoped>
.property-behavior {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Os dois limites um embaixo do outro (pedido do usuário) */
.property-behavior-count {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

/* O interruptor e, ligado, o número logo depois dele */
.property-behavior-limit {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* O número entre o − e o + */
.property-behavior-number {
  flex: 0 0 150px;
}
</style>
